import { employeeBankDetailsSchema } from '@cpce/shared';
import { Pencil, Save } from 'lucide-react';
import { useState } from 'react';
import { useSetEmployeeBankDetails } from '@/api/employees/employees.mutations';
import { Button } from '@/components/Button';
import { CardWithTitle } from '@/components/CardWithTitle';
import { FieldsGrid } from '@/components/FieldsGrid';
import { Input } from '@/components/Input';
import { formatIban } from '@/utils/string.utils';

interface EmployeeBankDetailsCardProps {
  employeeId: number;
  iban: string | null;
  bic: string | null;
}

// Kept on our side — the billetterie card has none — and read by the SEPA file when a créance is
// paid by virement. Checked to the key before saving: one bad IBAN gets the whole file refused.
export function EmployeeBankDetailsCard(props: EmployeeBankDetailsCardProps) {
  const { employeeId, iban, bic } = props;
  const { mutate: saveBankDetails, isPending } = useSetEmployeeBankDetails(employeeId);
  const [form, setForm] = useState<{ iban: string; bic: string } | null>(null);

  const validation = form ? employeeBankDetailsSchema.safeParse(form) : null;
  const errorOf = (field: 'iban' | 'bic') =>
    validation?.success === false
      ? validation.error.issues.find((issue) => issue.path[0] === field)?.message
      : undefined;

  const save = async () => {
    if (!form || !validation?.success) return;
    await saveBankDetails(form);
    setForm(null);
  };

  return (
    <CardWithTitle
      title="Coordonnées bancaires"
      actions={
        form ? (
          <Button
            type="button"
            variant="color"
            icon={Save}
            onClick={save}
            isPending={isPending}
            disabled={!validation?.success}
          >
            Enregistrer
          </Button>
        ) : (
          <Button
            type="button"
            icon={Pencil}
            onClick={() => setForm({ iban: iban ? formatIban(iban) : '', bic: bic ?? '' })}
            className="text-gray-600"
          />
        )
      }
    >
      <FieldsGrid
        fields={[
          {
            title: 'IBAN',
            value: form ? (
              <div className="flex flex-col gap-1">
                <Input
                  value={form.iban}
                  onChange={(e) => setForm({ ...form, iban: e.target.value })}
                  placeholder="FR76 3000 6000 0112 3456 7890 189"
                  aria-invalid={!!errorOf('iban')}
                />
                {errorOf('iban') && <span className="text-xs text-red-600">{errorOf('iban')}</span>}
              </div>
            ) : (
              iban && formatIban(iban)
            ),
          },
          {
            title: 'BIC',
            value: form ? (
              <div className="flex flex-col gap-1">
                <Input
                  value={form.bic}
                  onChange={(e) => setForm({ ...form, bic: e.target.value })}
                  placeholder="Facultatif dans l'Espace économique européen"
                  aria-invalid={!!errorOf('bic')}
                />
                {errorOf('bic') && <span className="text-xs text-red-600">{errorOf('bic')}</span>}
              </div>
            ) : (
              bic
            ),
          },
        ]}
      />
    </CardWithTitle>
  );
}
